import type {
  BulkJobCrewResult,
  JobDto,
  JobStatus,
  JobSummaryDto,
  JobVisitDto,
  JobVisitWithCrewDto,
  SeriesEmployeePay,
  SeriesLaborRollup,
} from '@/lib/api/jobs';
import type { Translations } from '@/lib/i18n';
import { fillTemplate } from '@/lib/utils';

import { TODAY } from '../jobs/helpers';
import type { CrewBulkAction, VisitFilterState } from './types';

// Sum of each currently-assigned crew member's own standard per-visit rate
// (their total pay divided by their own assigned-visit count) — reflects the
// current crew, not diluted by visits that have no crew assigned yet.
export function laborPerVisit(labor: SeriesLaborRollup): number {
  return labor.by_employee.reduce(
    (sum, r) => (r.visits_assigned > 0 ? sum + r.total_pay / r.visits_assigned : sum),
    0,
  );
}

// The job's current default price per visit — the newest rate being charged,
// not a history-based average across past visits.
export function expectedPerVisit(job: Pick<JobDto, 'total_amount'> | undefined): number {
  return job?.total_amount ?? 0;
}

export function profitPerVisit(expected: number, labor: number): number {
  return expected - labor;
}

// Allowed quick-advance transition for a single visit. `null` = no forward
// move offered (draft has "start" but not "complete"; terminal states have
// nothing).
export function nextStatus(status: JobStatus): JobStatus | null {
  if (status === 'draft') return 'started';
  if (status === 'started') return 'completed';
  return null;
}

// Which month the visit calendar should open on: the next upcoming visit,
// else the first visit on record, else the current month.
export function initialVisitMonth(
  summary: JobSummaryDto | undefined,
  visits: JobVisitDto[],
): Date {
  const iso =
    summary?.next_visit_date ??
    summary?.first_visit_date ??
    [...visits].map((v) => v.scheduled_date).sort()[0];

  if (iso) {
    const [y, m] = iso.split('-').map(Number);
    if (y && m) return new Date(y, m - 1, 1);
  }

  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

export function isPartiallyPaid(visit: JobVisitDto): boolean {
  return (
    visit.payment_status === 'paid' &&
    visit.amount_collected != null &&
    visit.total_amount != null &&
    visit.amount_collected < visit.total_amount
  );
}

export function filterVisits(
  rows: JobVisitWithCrewDto[],
  filters: VisitFilterState,
): JobVisitWithCrewDto[] {
  return rows.filter((row) => {
    if (filters.status !== 'all' && row.status !== filters.status) return false;

    if (filters.payment === 'paid' && row.payment_status !== 'paid') return false;
    if (filters.payment === 'unpaid' && row.payment_status !== 'unpaid') {
      return false;
    }
    if (filters.payment === 'partial' && !isPartiallyPaid(row)) return false;

    if (filters.date === 'past' && row.scheduled_date >= TODAY) return false;
    if (filters.date === 'upcoming' && row.scheduled_date < TODAY) return false;

    return true;
  });
}

// The crew roster shown in the management bar comes from the server-computed
// summary, not the visit rows.
export function rosterFromSummary(
  byEmployee: SeriesEmployeePay[] | undefined,
): SeriesEmployeePay[] {
  return [...(byEmployee ?? [])].sort((a, b) =>
    a.full_name.localeCompare(b.full_name),
  );
}

interface CrewToast {
  tone: 'success' | 'message';
  text: string;
}

// Turns a {visits_matched, rows_affected} result into a toast. An empty scope
// ({0,0}) and "nothing to do" (rows_affected 0) are both successes, not
// errors — they just get a neutral message instead of a green check.
export function crewResultMessage(
  result: BulkJobCrewResult,
  action: CrewBulkAction,
  employeeName: string,
  t: Translations,
): CrewToast {
  if (result.visits_matched === 0) {
    return { tone: 'message', text: t.seriesCrewNoMatch };
  }
  if (result.rows_affected === 0) {
    return {
      tone: 'message',
      text: action === 'change-pay' ? t.seriesPayNoChange : t.seriesCrewAlreadyAll,
    };
  }

  const n = result.rows_affected;
  if (action === 'add') {
    return {
      tone: 'success',
      text: fillTemplate(t.seriesCrewAddedTo, { name: employeeName, n }),
    };
  }
  if (action === 'change-pay') {
    return { tone: 'success', text: fillTemplate(t.seriesPayUpdatedOn, { n }) };
  }
  return {
    tone: 'success',
    text: fillTemplate(t.seriesCrewRemovedFrom, { name: employeeName, n }),
  };
}
